/**
 * DESAFIO DOS CAMPEÕES — MODO PALCO V2
 *
 * Tela exclusiva para TV / Datashow / Telão. Otimizada para 10m de distância.
 * Separa Tela do Apresentador (esta) da Tela da Plateia (ArenaTV existente).
 *
 * - Auto-scale inteligente da pergunta (cabe textos curtos/médios/longos)
 * - Timer ring animado verde/amarelo/vermelho com pulse final
 * - Alternativas Kahoot-style 2x2 (vermelho/azul/amarelo/verde)
 * - Top 4 ranking pulsando em mudanças
 * - Botão "PRÓXIMA PERGUNTA" gigante + atalhos SPACE/ENTER
 * - Botão fullscreen TV mode
 *
 * Rota: /arena/palco/:code
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Trophy, SkipForward, Maximize2 } from 'lucide-react';
import api from '../lib/api';
import './ArenaPalco.css';

function useArenaState(code, pollMs = 1500) {
  const [state, setState] = useState(null);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get(`/champions/arena/sessions/${code}/state`);
        if (alive) setState(r.data);
      } catch { /* noop */ }
    };
    tick();
    const id = setInterval(tick, pollMs);
    return () => { alive = false; clearInterval(id); };
  }, [code, pollMs]);
  return state;
}

// Calcula tempo restante a partir do current_question_revealed_at + question_duration_seconds
function useCountdown(state) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, []);
  if (!state?.current_question_revealed_at) return null;
  const dur = state.question_duration_seconds || state.current_question?.tempo_segundos || 30;
  try {
    const start = new Date(state.current_question_revealed_at).getTime();
    const elapsed = (now - start) / 1000;
    return Math.max(0, Math.min(dur, dur - elapsed));
  } catch { return null; }
}

// Auto-scale: reduz font-size se o texto da pergunta não couber
function AutoScaleText({ children, baseSize = 64, minSize = 26 }) {
  const ref = useRef(null);
  const [size, setSize] = useState(baseSize);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let s = baseSize;
    el.style.fontSize = `${s}px`;
    // reduz até caber sem overflow vertical (parent é o container .palco-question)
    const parent = el.parentElement;
    if (!parent) return;
    let guard = 60;
    while ((el.scrollHeight > parent.clientHeight - 30 || el.scrollWidth > parent.clientWidth - 30) && s > minSize && guard-- > 0) {
      s -= 2;
      el.style.fontSize = `${s}px`;
    }
    setSize(s);
  }, [children, baseSize, minSize]);
  return <div ref={ref} className="palco-question-text" style={{ fontSize: size }}>{children}</div>;
}

function TimerRing({ seconds, total }) {
  if (seconds == null) return null;
  const radius = 46;
  const circ = 2 * Math.PI * radius;
  const pct = total > 0 ? Math.max(0, Math.min(1, seconds / total)) : 0;
  const dashOffset = circ * (1 - pct);
  const color = seconds > 10 ? 'green' : seconds > 5 ? 'yellow' : 'red';
  return (
    <div className="palco-timer" data-testid="palco-timer">
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle className="track" cx="55" cy="55" r={radius} strokeWidth="9" />
        <circle
          className={`progress ${color}`}
          cx="55" cy="55" r={radius} strokeWidth="9"
          strokeDasharray={`${circ * pct} ${circ}`}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
        />
      </svg>
      <div className="label">{Math.ceil(seconds)}</div>
    </div>
  );
}

// Som "tique-tique" nos últimos 5s via Web Audio API (zero MB de áudio)
function useTickSound(remaining) {
  const ctxRef = useRef(null);
  const lastTickRef = useRef(-1);
  // Inicializa AudioContext na primeira interação (workaround políticas de autoplay)
  useEffect(() => {
    const init = () => {
      if (!ctxRef.current) {
        try { ctxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch { /* noop */ }
      }
    };
    window.addEventListener('pointerdown', init, { once: true });
    window.addEventListener('keydown', init, { once: true });
    return () => {
      window.removeEventListener('pointerdown', init);
      window.removeEventListener('keydown', init);
    };
  }, []);
  useEffect(() => {
    if (remaining == null) { lastTickRef.current = -1; return; }
    const sec = Math.ceil(remaining);
    if (sec === lastTickRef.current) return;
    lastTickRef.current = sec;
    if (sec > 5 || sec < 0) return;
    const ctx = ctxRef.current;
    if (!ctx) return;
    try {
      const isFinal = sec === 0;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = isFinal ? 220 : (sec <= 2 ? 880 : 660);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(isFinal ? 0.20 : 0.10, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + (isFinal ? 0.55 : 0.10));
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + (isFinal ? 0.6 : 0.15));
    } catch { /* noop */ }
  }, [remaining]);
  return ctxRef;
}

// Acordes ACERTOU 🎉 (Maior C-E-G ascendente) / ERROU 😞 (Menor C-Eb-G descendente)
function playResultChord(ctx, correct) {
  if (!ctx) return;
  try {
    // Acertou: C5 - E5 - G5 arpejado ascendente (alegre)
    // Errou: C5 - Eb5 - G4 descendente (triste/fanfare drop)
    const notes = correct
      ? [{ f: 523, t: 0.00 }, { f: 659, t: 0.08 }, { f: 784, t: 0.16 }, { f: 1047, t: 0.30 }]
      : [{ f: 523, t: 0.00 }, { f: 466, t: 0.18 }, { f: 392, t: 0.40 }];
    notes.forEach(({ f, t }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = correct ? 'triangle' : 'sine';
      osc.frequency.value = f;
      const startAt = ctx.currentTime + t;
      const duration = correct ? 0.35 : 0.55;
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(correct ? 0.20 : 0.18, startAt + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start(startAt);
      osc.stop(startAt + duration + 0.05);
    });
  } catch { /* noop */ }
}

export default function ArenaPalco() {
  const { code } = useParams();
  const state = useArenaState(code);
  const remaining = useCountdown(state);
  const containerRef = useRef(null);
  const prevRankRef = useRef([]);
  const ctxRef = useTickSound(state?.status === 'playing' ? remaining : null);
  const prevIndexRef = useRef(null);
  const prevQuestionIdRef = useRef(null);

  // Atalhos SPACE/ENTER para próxima pergunta
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        next();
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [code]);

  const next = async () => {
    try { await api.post(`/champions/arena/sessions/${code}/next`); }
    catch { /* noop */ }
  };

  // Detecta avanço de questão e toca acorde de ACERTOU/ERROU baseado nas respostas da questão anterior
  useEffect(() => {
    if (!state) return;
    const idx = state.current_index ?? 0;
    const prev = prevIndexRef.current;
    const prevQid = prevQuestionIdRef.current;
    // Houve avanço (não é o primeiro carregamento)
    if (prev != null && idx > prev && prevQid) {
      const answers = state.answers?.[prevQid] || {};
      const arr = Object.values(answers);
      if (arr.length > 0) {
        const correctCount = arr.filter((a) => a.correct).length;
        const hitRate = correctCount / arr.length;
        // ACERTOU se ≥50% acertou, ERROU caso contrário
        playResultChord(ctxRef.current, hitRate >= 0.5);
      }
    }
    prevIndexRef.current = idx;
    if (state.current_question?.id) {
      prevQuestionIdRef.current = state.current_question.id;
    }
  }, [state, ctxRef]);

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  if (!state) {
    return <div className="palco-bg" style={{ display: 'grid', placeItems: 'center' }}>
      <div style={{ fontSize: 32, fontWeight: 700 }}>Carregando arena…</div>
    </div>;
  }

  const q = state.current_question;
  const idx = (state.current_index ?? 0) + 1;
  const total = state.total_questions ?? 0;
  const dur = state.question_duration_seconds || q?.tempo_segundos || 30;
  const ended = state.status === 'ended';
  const lobby = state.status === 'lobby';
  const ranking = (state.ranking || []).slice(0, 4);
  // marca posições com alteração para pulse
  const rankIdsBefore = prevRankRef.current.map((r) => r.id).join(',');
  const rankIdsNow = ranking.map((r) => r.id).join(',');
  const changed = rankIdsBefore !== rankIdsNow;
  prevRankRef.current = ranking;

  // Última pontuação
  const lastAnswer = (() => {
    try {
      const allAnswers = state.answers || {};
      const lastKey = Object.keys(allAnswers).pop();
      if (!lastKey) return null;
      const qa = allAnswers[lastKey] || {};
      let bestDelta = 0; let bestName = '';
      for (const [pid, a] of Object.entries(qa)) {
        if ((a.score_delta || 0) > bestDelta) {
          bestDelta = a.score_delta;
          const p = (state.participants || []).find((x) => x.id === pid);
          bestName = p?.name || '';
        }
      }
      return bestDelta > 0 ? { delta: bestDelta, name: bestName } : null;
    } catch { return null; }
  })();

  return (
    <div className="palco-bg" ref={containerRef} data-testid="arena-palco">
      {ended && Array.from({ length: 40 }).map((_, i) => (
        <div key={i} className="palco-confetti" style={{
          left: `${Math.random() * 100}%`,
          background: ['#f43f5e', '#fbbf24', '#22c55e', '#6366f1', '#06b6d4'][i % 5],
          animationDelay: `${Math.random() * 2}s`,
        }} />
      ))}

      <div className="palco-container">
        {/* ── HEADER ── */}
        <div className="palco-header">
          <div className="palco-title" data-testid="palco-title">
            <Trophy className="w-9 h-9" style={{ color: '#fbbf24' }} />
            DESAFIO DOS CAMPEÕES
            <span className="badge">PALCO</span>
          </div>
          {!lobby && !ended && total > 0 && (
            <div className="palco-progress" data-testid="palco-progress">
              <span>PERGUNTA</span>
              <span className="num">{idx}</span>
              <span>DE</span>
              <span className="num">{total}</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            {!lobby && !ended && <TimerRing seconds={remaining} total={dur} />}
            <button className="palco-fullscreen" onClick={toggleFullscreen} data-testid="palco-fullscreen">
              <Maximize2 className="w-4 h-4" /> Tela Cheia <span style={{ opacity: 0.6 }}>(F)</span>
            </button>
          </div>
        </div>

        {/* ── CENTRO (pergunta + alternativas) ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minHeight: 0 }}>
          {lobby && (
            <div className="palco-lobby">
              <div>
                <div className="palco-side-title">CÓDIGO DA SESSÃO</div>
                <div className="palco-code" data-testid="palco-code">{state.code}</div>
                <div style={{ textAlign: 'center', fontSize: 22, color: '#cbd5e1', marginTop: 12 }}>
                  Acesse <strong style={{ color: '#fff' }}>{window.location.origin.replace(/^https?:\/\//, '')}/arena/aluno/{state.code}</strong> no celular para entrar.
                </div>
              </div>
              <div>
                <div className="palco-side-title">PARTICIPANTES NA SALA</div>
                <div style={{ fontSize: 110, fontWeight: 900, textAlign: 'center', color: '#22c55e' }}>
                  {(state.participants || []).length}
                </div>
                <div style={{ textAlign: 'center', fontSize: 18, color: '#94a3b8' }}>
                  Esperando o apresentador iniciar…
                </div>
              </div>
            </div>
          )}

          {!lobby && !ended && q && (
            <>
              <div className="palco-question" data-testid="palco-question">
                <AutoScaleText baseSize={64} minSize={26}>{q.enunciado || '—'}</AutoScaleText>
              </div>
              <div className="palco-alts" data-testid="palco-alts">
                {['a', 'b', 'c', 'd'].map((l) => {
                  const txt = q[`alternativa_${l}`];
                  if (!txt) return null;
                  return (
                    <div key={l} className="palco-alt" data-testid={`palco-alt-${l}`}>
                      <div className="letter">{l.toUpperCase()}</div>
                      <div className="txt">{txt}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {ended && (
            <div style={{ display: 'grid', placeItems: 'center', textAlign: 'center', flex: 1 }}>
              <div>
                <Trophy size={120} style={{ color: '#fbbf24', filter: 'drop-shadow(0 0 40px rgba(251,191,36,0.5))' }} />
                <div style={{ fontSize: 80, fontWeight: 900, marginTop: 18, letterSpacing: '-0.02em' }}>BATALHA ENCERRADA!</div>
                {ranking[0] && (
                  <div style={{ fontSize: 36, color: '#fbbf24', marginTop: 12 }}>
                    🏆 Campeão: <strong>{ranking[0].name}</strong> com <strong>{ranking[0].score}</strong> pts
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── SIDEBAR (Top 4 + status + última pontuação) ── */}
        <div className="palco-side">
          <div className="palco-side-block">
            <div className="palco-side-title">🏆 Top 4</div>
            {ranking.length === 0 && <div style={{ color: '#94a3b8', textAlign: 'center', padding: 12 }}>Sem pontuações ainda.</div>}
            {ranking.map((r, i) => {
              const cls = i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : '';
              return (
                <div key={r.id} className={`palco-rank-item ${cls} ${changed ? 'pulse' : ''}`} data-testid={`palco-rank-${r.rank}`}>
                  <div className="palco-rank-pos">{r.rank}º</div>
                  <div className="palco-rank-name">{r.name}</div>
                  <div className="palco-rank-score">{r.score}</div>
                </div>
              );
            })}
          </div>

          {lastAnswer && (
            <div className="palco-side-block" data-testid="palco-last-answer">
              <div className="palco-side-title">💥 Última pontuação</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{lastAnswer.name}</div>
              <div style={{ fontSize: 32, fontWeight: 900, color: '#22c55e' }}>+{lastAnswer.delta}</div>
            </div>
          )}

          <div className={`palco-status ${state.status === 'paused' ? 'paused' : ended ? 'ended' : ''}`}>
            {state.status === 'lobby' && 'AGUARDANDO INÍCIO'}
            {state.status === 'playing' && 'EM RODADA'}
            {state.status === 'paused' && 'PAUSADO'}
            {state.status === 'ended' && '🎉 ENCERRADO'}
          </div>
        </div>

        {/* ── FOOTER (Próxima Pergunta) ── */}
        {!ended && (
          <div className="palco-footer">
            <button className="palco-next-btn" onClick={next} data-testid="palco-next-btn">
              <SkipForward className="w-7 h-7" />
              {lobby ? 'INICIAR BATALHA' : 'PRÓXIMA PERGUNTA'}
              <span className="shortcut">SPACE / ENTER</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
